import { logger } from '@/utils/logger/logger';
import {
  finishTracking,
  type SessionFlowFailure,
  startTracking,
  type TrackingHandle,
  TrackingKey,
} from '@heidi/runtime/performance';

// `transcription_finalize` client metrics: one attempt per v3 delivery whose
// transcription the client watches settle, matching the backend's one attempt
// per stream. Autonomous after the stop, so no intent.
// The attempt opens when the stop step fixes the last chunk index, and closes
// when the chunk-response stream settles every index or ends unfinished.
// A page exit mid-transcription replays as abandoned: nothing resumes the
// stream for a delivery whose stop step already ran.
// Not covered: the v1 and v2 HTTP pipelines and desktop, which observe no
// recording-level transcription result, and deliveries gapped at stop, which
// recording_finalize already fails. Details in monitoring/rum_metrics.tf.

export type TranscriptionFinalizeErrorName =
  | 'TranscriptionFinalizeNoUsableFragments'
  | 'TranscriptionFinalizeIncomplete'
  | 'TranscriptionFinalizeForbidden'
  | 'TranscriptionFinalizeSessionDeleted'
  | 'TranscriptionFinalizeSignedOut'
  | 'TranscriptionFinalizeUnclassified';

/**
 * The `transcription_finalize` failure vocabulary, registered with the rest of
 * the session-flow names. `TranscriptionFinalizeUnclassified` is absent on
 * purpose, so it lands in `_OTHER`.
 */
export const TRANSCRIPTION_FINALIZE_FAILURE_TYPES: Readonly<
  Record<
    Exclude<TranscriptionFinalizeErrorName, 'TranscriptionFinalizeUnclassified'>,
    SessionFlowFailure
  >
> = {
  // Every expected chunk settled as failed: the backend's ERROR bucket.
  TranscriptionFinalizeNoUsableFragments: {
    outcome: 'failed',
    errorType: 'no_usable_fragments',
  },
  // The stream's window closed before every expected chunk settled.
  TranscriptionFinalizeIncomplete: {
    outcome: 'failed',
    errorType: 'transcription_incomplete',
  },
  // Lost access to the session: a precondition, not a transcription fault.
  TranscriptionFinalizeForbidden: { outcome: 'blocked', errorType: 'permission_denied' },
  TranscriptionFinalizeSessionDeleted: { outcome: 'cancelled' },
  // Another account signed in, so this page stopped watching.
  TranscriptionFinalizeSignedOut: { outcome: 'cancelled' },
};

const namedError = (name: TranscriptionFinalizeErrorName): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

// Telemetry must never change the stream's behaviour; log the class only.
const safely = (step: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    logger.warn('[transcriptionFinalizeTracking] telemetry step failed', {
      step,
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
};

/** The stop step fixed the last chunk index: the attempt opens here. */
export const beginTranscriptionFinalizeAttempt = (): TrackingHandle | undefined => {
  let handle: TrackingHandle | undefined;
  safely('begin', () => {
    handle = startTracking({
      key: TrackingKey.SCRIBE_TRANSCRIPTION_FINALIZE,
      instanced: true,
      metadata: { trigger: 'stop_marker' },
    });
  });
  return handle;
};

export type TranscriptionSettledCounts = {
  /** Expected chunks: index 0 through the last chunk index. */
  expected: number;
  transcribed: number;
  failed: number;
};

/**
 * Every expected chunk settled. A chunk settled only through delivery progress
 * has no observed kind, so the attempt fails only when every chunk was seen to
 * fail, as the backend fails only a stream with no usable fragment.
 */
export const completeTranscriptionFinalizeAttempt = (
  handle: TrackingHandle | undefined,
  counts: TranscriptionSettledCounts
): void =>
  safely('complete', () => {
    if (!handle) return;
    const metadata = {
      expected_chunks: counts.expected,
      failed_chunks: counts.failed,
      unobserved_chunks: Math.max(
        0,
        counts.expected - counts.transcribed - counts.failed
      ),
    };
    if (counts.expected > 0 && counts.failed === counts.expected) {
      finishTracking({
        handle,
        outcome: 'failed',
        error: namedError('TranscriptionFinalizeNoUsableFragments'),
        metadata: { result: 'error', ...metadata },
      });
      return;
    }
    finishTracking({ handle, metadata: { result: 'success', ...metadata } });
  });

/** Why a chunk-response stream ended before every expected chunk settled. */
export type TranscriptionStreamEnd =
  | 'access_denied'
  | 'session_deleted'
  | 'signed_out'
  | 'window_expired'
  | 'unavailable';

const FAILED_CLOSE_BY_END: Record<
  TranscriptionStreamEnd,
  { name: TranscriptionFinalizeErrorName; cancelReason?: string }
> = {
  access_denied: { name: 'TranscriptionFinalizeForbidden' },
  session_deleted: {
    name: 'TranscriptionFinalizeSessionDeleted',
    cancelReason: 'session_deleted',
  },
  signed_out: { name: 'TranscriptionFinalizeSignedOut', cancelReason: 'signed_out' },
  window_expired: { name: 'TranscriptionFinalizeIncomplete' },
  unavailable: { name: 'TranscriptionFinalizeUnclassified' },
};

/** The stream ended unfinished: nothing more can arrive on this page. */
export const endTranscriptionFinalizeAttempt = (
  handle: TrackingHandle | undefined,
  end: TranscriptionStreamEnd
): void =>
  safely('end', () => {
    if (!handle) return;
    const close = FAILED_CLOSE_BY_END[end];
    finishTracking({
      handle,
      outcome: 'failed',
      error: namedError(close.name),
      metadata: {
        result: 'error',
        stream_end: end,
        ...(close.cancelReason ? { cancel_reason: close.cancelReason } : {}),
      },
    });
  });
